document.addEventListener('alpine:init', () => {
  Alpine.data(
    'resultsComponent',
    (applicationId, apiKey, indexName, preloadedProducts = null) => ({
      searchQuery: '',
      results: [],
      filteredResults: [],
      isLoading: false,
      selectedSort: 'relevance',
      categories: [],
      selectedCategories: [],
      mobileFiltersOpen: false,
      filters: {},

      // Pagination (client-side)
      currentPage: 0,
      itemsPerPage: 20,
      selectedResultsPerPage: 20,
      resultsPerPageOptions: [20, 30, 50, 100],
      allResults: [], // All results from server or preloaded
      paginatedResults: [], // Current page results
      totalPages: 0,

      // Tag-based filters
      tagFilterPrefixes: ['cat:', 'brand:', 'for:'],
      tagFilters: {
        'cat:': { name: 'Kategorie', options: [], selected: [] },
        'brand:': { name: 'Passend für Marke', options: [], selected: [] },
        'for:': { name: 'Passend für', options: [], selected: [] },
      },
      sortOptions: [
        { value: 'relevance', label: 'Relevance' },
        { value: 'name-asc', label: 'Name A-Z' },
        { value: 'name-desc', label: 'Name Z-A' },
        { value: 'price-asc', label: 'Price Low to High' },
        { value: 'price-desc', label: 'Price High to Low' },
      ],

      // Algolia configuration
      algolia: {
        applicationId: applicationId,
        apiKey: apiKey,
        indexName: indexName,
        baseUrl: applicationId
          ? `https://${applicationId}.algolia.net`
          : null,
      },

      // Data source mode
      usePreloadedProducts: preloadedProducts !== null,
      preloadedProducts: preloadedProducts,

      init() {
        // Get search query from URL parameters
        const urlParams = new URLSearchParams(
          window.location.search,
        );
        this.searchQuery = urlParams.get('q') || '';

        // Initialize products based on source
        if (this.usePreloadedProducts) {
          this.initializeFromPreloadedProducts();
          console.log('Initialized from preloaded products');
          console.log(this.preloadedProducts);
        } else if (this.searchQuery) {
          this.performSearch();
        }

        // Watch for sort changes
        this.$watch('selectedSort', () => {
          if (this.filteredResults.length > 0) {
            this.applyFiltersAndSort();
          }
        });

        // Watch for category changes
        this.$watch('selectedCategories', () => {
          this.currentPage = 0;
          this.applyFiltersAndSort();
        });

        // Watch for tag filter changes
        this.tagFilterPrefixes.forEach((prefix) => {
          this.$watch(`tagFilters['${prefix}'].selected`, () => {
            this.currentPage = 0;
            this.applyFiltersAndSort();
          });
        });

        // Watch for results per page changes
        this.$watch('selectedResultsPerPage', () => {
          this.itemsPerPage = this.selectedResultsPerPage;
          this.currentPage = 0;
          this.updatePagination();
        });
      },

      /**
       * Initialize component with preloaded products
       */
      initializeFromPreloadedProducts() {
        if (!this.preloadedProducts || !Array.isArray(this.preloadedProducts)) {
          console.error('Invalid preloaded products data');
          this.allResults = [];
          this.filteredResults = [];
          this.paginatedResults = [];
          return;
        }

        // Transform preloaded products to expected format
        this.allResults = this.transformProducts(this.preloadedProducts);

        // Extract categories and tag filters from ALL results
        this.extractCategories();
        this.extractTagFilters();

        // Apply filters, sorting and pagination
        this.applyFiltersAndSort();

      },

      /**
       * Transform products to standardized format
       * Works with both Algolia hits and custom product objects
       */
      transformProducts(products) {
        return products.map((product, index) => {
          // Check if this is an Algolia hit or custom product
          const isAlgoliaHit = product.objectID !== undefined;

          if (isAlgoliaHit) {
            return this.transformAlgoliaHit(product, index);
          } else {
            return this.transformCustomProduct(product, index);
          }
        });
      },

      /**
       * Transform Algolia hit to standard format
       */
      transformAlgoliaHit(hit, index) {
        return {
          id: hit.objectID,
          sku: hit.sku || null,
          title: hit.title,
          url: hit.url || `/products/${hit.handle}`,
          oem: hit.meta?.custom?.oem_reference || null,
          fitsText: hit.meta?.custom?.passt_fuer || null,
          image: hit.product_image || hit.image,
          description:
            hit.body_html_safe?.slice(0, 120) + '...' || null,
          price: hit.price ? hit.price.toFixed(2) : null,
          available: hit.inventory_available || null,
          product_type: hit.product_type || null,
          tags: hit.tags || [],
          position: index,
        };
      },

      /**
       * Transform custom product object to standard format
       * Expects products with similar structure or adapts them
       */
      transformCustomProduct(product, index) {
        console.log(product);
        return {
          id: product.variants[0].id || product.objectID || index,
          sku: product.sku || null,
          title: product.title || product.name || '',
          url: product.url || `/products/${product.handle || product.id}`,
          oem: product.oem || product.meta?.custom?.oem_reference || null,
          fitsText: product.fitsText || product.meta?.custom?.passt_fuer || null,
          image: product.featured_image || product.product_image || null,
          description: product.description
            ? (product.description.replace(/<[^>]*>/g, '').slice(0, 120) + '...')
            : null,
          price: product.price
            ? (typeof product.price === 'number' ? (product.price / 100).toFixed(2) : product.price)
            : null,
          available: product.available !== undefined
            ? product.available
            : (product.inventory_available || null),
          product_type: product.type || product.category || null,
          tags: product.tags || [],
          position: product.position !== undefined ? product.position : index,
        };
      },

      extractCategories() {
        const categorySet = new Set();
        this.allResults.forEach((product) => {
          if (product.product_type) {
            categorySet.add(product.product_type);
          }
        });

        this.categories = Array.from(categorySet).map(
          (category) => ({
            name: category,
            value: category,
          }),
        );
      },

      extractTagFilters() {
        // Reset all tag filter options
        this.tagFilterPrefixes.forEach((prefix) => {
          this.tagFilters[prefix].options = [];
        });

        const tagSets = {};
        this.tagFilterPrefixes.forEach((prefix) => {
          tagSets[prefix] = new Set();
        });

        this.allResults.forEach((product) => {
          if (product.tags && Array.isArray(product.tags)) {
            product.tags.forEach((tag) => {
              this.tagFilterPrefixes.forEach((prefix) => {
                if (tag.startsWith(prefix)) {
                  const value = tag.substring(
                    prefix.length,
                  );
                  if (value.trim()) {
                    tagSets[prefix].add(value);
                  }
                }
              });
            });
          }
        });

        // Convert sets to options arrays
        this.tagFilterPrefixes.forEach((prefix) => {
          this.tagFilters[prefix].options = Array.from(
            tagSets[prefix],
          ).map((value) => ({
            name: value,
            value: value,
          }));
        });
      },

      filterResults() {
        let filtered = [...this.allResults];

        // Filter by categories
        if (this.selectedCategories.length > 0) {
          filtered = filtered.filter((product) =>
            this.selectedCategories.includes(
              product.product_type,
            ),
          );
        }

        // Filter by tag filters
        this.tagFilterPrefixes.forEach((prefix) => {
          const selectedTags = this.tagFilters[prefix].selected;
          if (selectedTags.length > 0) {
            filtered = filtered.filter((product) => {
              if (
                !product.tags ||
                !Array.isArray(product.tags)
              ) {
                return false;
              }
              return product.tags.some((tag) => {
                if (tag.startsWith(prefix)) {
                  const value = tag.substring(
                    prefix.length,
                  );
                  return selectedTags.includes(value);
                }
                return false;
              });
            });
          }
        });

        // Calculate total pages
        this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);

        this.filteredResults = filtered;
      },

      applyFiltersAndSort() {
        this.filterResults();
        this.sortResults();
        this.updatePagination();
      },

      sortResults() {
        switch (this.selectedSort) {
          case 'name-asc':
            this.filteredResults.sort((a, b) => {
              const comparison = a.title.localeCompare(
                b.title,
              );
              return comparison !== 0
                ? comparison
                : a.position - b.position;
            });
            break;
          case 'name-desc':
            this.filteredResults.sort((a, b) => {
              const comparison = b.title.localeCompare(
                a.title,
              );
              return comparison !== 0
                ? comparison
                : a.position - b.position;
            });
            break;
          case 'price-asc':
            this.filteredResults.sort((a, b) => {
              const comparison =
                parseFloat(a.price) - parseFloat(b.price);
              return comparison !== 0
                ? comparison
                : a.position - b.position;
            });
            break;
          case 'price-desc':
            this.filteredResults.sort((a, b) => {
              const comparison =
                parseFloat(b.price) - parseFloat(a.price);
              return comparison !== 0
                ? comparison
                : a.position - b.position;
            });
            break;
          case 'relevance':
          default:
            this.filteredResults.sort(
              (a, b) => a.position - b.position,
            );
            break;
        }
      },

      updatePagination() {
        const totalResults = this.filteredResults.length;
        const totalPages = Math.ceil(
          totalResults / this.itemsPerPage,
        );

        // Update pagination state
        this.totalPages = totalPages;
        this.totalResults = totalResults;

        // Ensure current page is valid
        if (this.currentPage >= totalPages) {
          this.currentPage = Math.max(0, totalPages - 1);
        }

        // Calculate paginated results
        const start = this.currentPage * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        this.paginatedResults = this.filteredResults.slice(
          start,
          end,
        );
      },

      // Pagination methods
      goToPage(page) {
        if (
          page >= 0 &&
          page < this.totalPages &&
          page !== this.currentPage
        ) {
          this.currentPage = page;
          this.updatePagination();
          // Scroll to top of the page
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },

      nextPage() {
        this.goToPage(this.currentPage + 1);
      },

      previousPage() {
        this.goToPage(this.currentPage - 1);
      },

      getPageNumbers() {
        const pages = [];
        const current = this.currentPage;
        const total = this.totalPages;

        // Always show first page
        if (total > 0) {
          pages.push(0);
        }

        // Show pages around current page
        let start = Math.max(1, current - 2);
        let end = Math.min(total - 1, current + 2);

        // Add ellipsis if needed between first page and range
        if (start > 1) {
          pages.push('...');
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
          if (i !== 0 && i !== total - 1) {
            pages.push(i);
          }
        }

        // Add ellipsis if needed between range and last page
        if (end < total - 2) {
          pages.push('...');
        }

        // Always show last page (if different from first)
        if (total > 1) {
          pages.push(total - 1);
        }

        return pages;
      },

      async performSearch() {
        if (!this.searchQuery.trim()) return;

        this.isLoading = true;

        // Fetch ALL results - no server-side filtering or pagination
        const searchBody = {
          query: this.searchQuery,
          hitsPerPage: 1000, // Get all results (adjust if you have more than 1000 products)
        };

        try {
          const response = await fetch(
            `${this.algolia.baseUrl}/1/indexes/${this.algolia.indexName}/query`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Algolia-Application-Id':
                  this.algolia.applicationId,
                'X-Algolia-API-Key': this.algolia.apiKey,
              },
              body: JSON.stringify(searchBody),
            },
          );

          if (!response.ok) {
            throw new Error(
              `Algolia API error: ${response.status}`,
            );
          }

          const data = await response.json();

          // Transform Algolia results
          this.allResults = this.transformProducts(data.hits);

          // Extract categories and tag filters from ALL results
          this.extractCategories();
          this.extractTagFilters();

          // Apply filters, sorting and pagination
          this.applyFiltersAndSort();

        } catch (error) {
          console.error('Algolia search error:', error);
          this.allResults = [];
          this.filteredResults = [];
          this.paginatedResults = [];
        }

        this.isLoading = false;
      },

      toggleCategory(categoryValue) {
        const index =
          this.selectedCategories.indexOf(categoryValue);
        if (index > -1) {
          this.selectedCategories.splice(index, 1);
        } else {
          this.selectedCategories.push(categoryValue);
        }
      },

      clearCategoryFilters() {
        this.selectedCategories = [];
      },

      isCategorySelected(categoryValue) {
        return this.selectedCategories.includes(categoryValue);
      },

      toggleTagFilter(prefix, value) {
        const selected = this.tagFilters[prefix].selected;
        const index = selected.indexOf(value);
        if (index > -1) {
          selected.splice(index, 1);
        } else {
          selected.push(value);
        }
      },

      clearTagFilters(prefix) {
        this.tagFilters[prefix].selected = [];
      },

      isTagFilterSelected(prefix, value) {
        return this.tagFilters[prefix].selected.includes(value);
      },

      getAllFilterGroups() {
        const groups = [];

        // Add category filter group (non-collapsible, always visible)
        groups.push({
          key: 'category',
          name: 'Produktart',
          visible: true,
          collapsible: false,
          open: true,
          options: this.categories,
          hasActiveFilters: this.selectedCategories.length > 0,
          toggleFunction: (value) => this.toggleCategory(value),
          isSelectedFunction: (value) => this.isCategorySelected(value),
          clearFunction: () => this.clearCategoryFilters(),
          type: 'category'
        });

        // Add tag filter groups (non-collapsible, visible when options exist)
        this.tagFilterPrefixes.forEach(prefix => {
          const tagFilter = this.tagFilters[prefix];
          if (tagFilter.options.length > 0) {
            groups.push({
              key: prefix,
              name: tagFilter.name,
              visible: true,
              collapsible: false,
              open: true,
              options: tagFilter.options,
              hasActiveFilters: tagFilter.selected.length > 0,
              toggleFunction: (value) => this.toggleTagFilter(prefix, value),
              isSelectedFunction: (value) => this.isTagFilterSelected(prefix, value),
              clearFunction: () => this.clearTagFilters(prefix),
              clearButtonText: 'Delete filters',
              type: 'tag'
            });
          }
        });

        // Add other filter groups (collapsible, from filters object)
        Object.keys(this.filters).forEach(filterKey => {
          const filter = this.filters[filterKey];
          const hasActiveFilters = filter.options.some(option => option.checked);

          groups.push({
            key: filterKey,
            name: filter.name,
            visible: true,
            collapsible: true,
            open: filter.desktopOpen || false,
            options: filter.options,
            hasActiveFilters: hasActiveFilters,
            toggleFunction: null, // Uses x-model in template
            isSelectedFunction: null, // Uses x-model in template
            clearFunction: () => {
              filter.options.forEach(option => {
                option.checked = false;
              });
            },
            clearButtonText: 'Clear',
            type: 'standard'
          });
        });

        return groups;
      },
    }),
  );
});
